import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { XCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';
import type { MessageDeliveryError } from '../../types/templateMessage.ts';

export interface DeliveryFailureHintProps {
  error?: MessageDeliveryError | null;
  isOwn?: boolean;
  compact?: boolean;
  triggerClassName?: string;
}

const LEGACY_REASON = 'Motivo não informado. Falha de entrega registrada sem detalhes.';
const EDGE = 16;
const GAP = 8;

/** Aviso não modal: hover/foco no desktop e toque persistente no celular. */
export default function DeliveryFailureHint({
  error, isOwn = false, compact = false, triggerClassName = '',
}: DeliveryFailureHintProps) {
  const { isDark } = useTheme();
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const pinned = useRef(false);
  const closing = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: EDGE, left: EDGE, width: 336, maxHeight: 400 });

  const clearClosing = useCallback(() => {
    if (closing.current !== null) clearTimeout(closing.current);
    closing.current = null;
  }, []);
  const close = useCallback(() => {
    clearClosing();
    pinned.current = false;
    setOpen(false);
  }, [clearClosing]);
  const show = () => { clearClosing(); setOpen(true); };
  const closeAfterHover = () => {
    clearClosing();
    if (pinned.current || document.activeElement === trigger.current) return;
    closing.current = setTimeout(close, 180);
  };
  const handleBlur = (event: React.FocusEvent<HTMLElement>) => {
    const next = event.relatedTarget as Node | null;
    if (next && (trigger.current?.contains(next) || panel.current?.contains(next))) return;
    if (pinned.current) return;
    close();
  };

  useEffect(() => clearClosing, [clearClosing]);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const anchor = isOwn && !compact
        ? trigger.current?.closest<HTMLElement>('[data-delivery-popup-anchor]') : null;
      const rect = (anchor || trigger.current)?.getBoundingClientRect();
      if (!rect) return;
      const gap = anchor && anchor !== trigger.current ? 16 : GAP;
      const viewport = window.visualViewport;
      const originX = viewport?.offsetLeft || 0;
      const originY = viewport?.offsetTop || 0;
      const viewportWidth = viewport?.width || window.innerWidth;
      const viewportHeight = viewport?.height || window.innerHeight;
      const width = Math.min(336, Math.max(0, viewportWidth - EDGE * 2));
      const maxHeight = Math.max(0, viewportHeight - EDGE * 2);
      const height = Math.min(panel.current?.getBoundingClientRect().height || 152, maxHeight);
      const minX = originX + EDGE;
      const minY = originY + EDGE;
      const maxX = Math.max(minX, originX + viewportWidth - width - EDGE);
      const maxY = Math.max(minY, originY + viewportHeight - height - EDGE);
      const hasRoomOnLeft = rect.left - width - gap >= minX;
      const left = hasRoomOnLeft ? rect.left - width - gap : rect.left;
      const top = hasRoomOnLeft ? rect.bottom - height :
        rect.bottom + gap + height <= originY + viewportHeight - EDGE ? rect.bottom + gap : rect.top - height - gap;
      setPosition({ left: Math.max(minX, Math.min(left, maxX)),
                    top: Math.max(minY, Math.min(top, maxY)), width, maxHeight });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [open, isOwn, compact, error?.message, error?.title, error?.code]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!trigger.current?.contains(target) && !panel.current?.contains(target)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener('pointerdown', outside, true);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside, true);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open, close]);

  const message = (typeof error?.message === 'string' && error.message.trim()) ||
    (typeof error?.title === 'string' && error.title.trim()) || LEGACY_REASON;
  const code = typeof error?.code === 'number' && Number.isFinite(error.code) ? error.code : null;
  const isMeta = error?.provider === 'official_whatsapp';
  const triggerColor = isOwn ? 'text-sminy-dark-danger hover:bg-white/10' :
    isDark ? 'text-sminy-dark-danger hover:bg-sminy-dark-elevated' : 'text-sminy-danger hover:bg-sminy-danger/10';

  return <>
    <button
      ref={trigger}
      type="button"
      aria-label="Ver motivo da falha de entrega"
      aria-expanded={open}
      aria-describedby={open ? id : undefined}
      onMouseEnter={show}
      onMouseLeave={closeAfterHover}
      onFocus={show}
      onBlur={handleBlur}
      onPointerDown={event => event.stopPropagation()}
      onKeyDown={event => {
        if (event.key === 'Tab') close();
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
      }}
      onClick={event => {
        event.stopPropagation();
        if (pinned.current) close();
        else { pinned.current = true; show(); }
      }}
      className={`relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl focus-visible:outline-none focus-visible:ring-2 ${isOwn ? 'focus-visible:ring-white/70' : 'focus-visible:ring-sminy-primary/40'} ${compact ? '-my-4 -ml-4 -mr-3' : ''} ${triggerColor} ${triggerClassName}`}
    >
      <XCircle aria-hidden="true" className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
    </button>
    {open && createPortal(
      <div
        id={id}
        ref={panel}
        role="tooltip"
        aria-label="Falha de entrega"
        style={position}
        onMouseEnter={clearClosing}
        onMouseLeave={closeAfterHover}
        onBlur={handleBlur}
        onPointerDown={event => event.stopPropagation()}
        onClick={event => event.stopPropagation()}
        className={`fixed z-[9998] overflow-y-auto break-words rounded-2xl border p-4 shadow-lg ${
          isDark ? 'border-sminy-dark-border bg-sminy-dark-surface text-sminy-dark-text-primary' :
            'border-sminy-border bg-white text-sminy-text-primary'}`}
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          <XCircle aria-hidden="true" className={`h-4 w-4 shrink-0 ${isDark ? 'text-sminy-dark-danger' : 'text-sminy-danger'}`} />
          <span>Falha de entrega</span>
        </div>
        <p className={`mt-3 text-xs leading-relaxed ${isDark ? 'text-sminy-dark-text-secondary' : 'text-sminy-text-secondary'}`}>{message}</p>
        {code !== null && <p className={`mt-6 text-xs ${isDark ? 'text-sminy-dark-muted' : 'text-sminy-muted'}`}>{isMeta ? 'Código Meta' : 'Código'}: {code}</p>}
      </div>, document.body,
    )}
  </>;
}
